import { FC, ReactNode } from "react";
import { css, keyframes } from "@emotion/react";
import { Box, SxProps, Theme } from "@mui/material";
import {
  BRANCH_FADE_MS,
  fadeIn,
  SKELETON_BG,
  SKELETON_SHOW_DELAY_MS,
} from "./stateTransitionStyles";

export interface SkeletonRevealProps {
  label: string;
  children: ReactNode;
}

/**
 * 스켈레톤 묶음 — 빠르게 끝나는 조회에서 깜빡이지 않도록 150ms 늦게 나타난다.
 * 묶음 전체가 하나의 로딩 상태로 읽히게 `role="status"` 와 `aria-busy` 를 단다.
 */
export const SkeletonReveal: FC<SkeletonRevealProps> = ({ label, children }) => (
  <Box role="status" aria-busy="true" aria-label={label} css={delayedAppearCss}>
    {children}
  </Box>
);

export interface SkeletonBoneProps {
  width: number | string;
  height: number;
  radius?: number;
  sx?: SxProps<Theme>;
}

export const SkeletonBone: FC<SkeletonBoneProps> = ({
  width,
  height,
  radius = 4,
  sx,
}) => (
  <Box
    aria-hidden="true"
    css={bonePulseCss}
    sx={{
      width,
      height,
      borderRadius: `${radius}px`,
      backgroundColor: SKELETON_BG,
      flexShrink: 0,
      ...sx,
    }}
  />
);

const pulse = keyframes`
  0%, 100% { opacity: 1; }
  50% { opacity: 0.45; }
`;

const bonePulseCss = css`
  display: block;
  animation: ${pulse} 1.4s ease-in-out infinite;
  @media (prefers-reduced-motion: reduce) {
    animation: none;
  }
`;

const delayedAppearCss = css`
  display: flex;
  flex-direction: column;
  animation: ${fadeIn} ${BRANCH_FADE_MS}ms ease ${SKELETON_SHOW_DELAY_MS}ms both;
  @media (prefers-reduced-motion: reduce) {
    animation: none;
  }
`;
